import React, { useState } from 'react';
import { Check, ArrowRight, ShieldCheck, Zap, MessageSquare, Phone, Lock, Sparkles, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Pricing: React.FC = () => {
  // Simple interactive estimator state
  const [calcUsers, setCalcUsers] = useState<number>(200);
  const [calcSms, setCalcSms] = useState<number>(50);
  const [calcWa, setCalcWa] = useState<number>(50);

  // Suggested tier calculation
  let suggestedPlan = 'Free';
  let basePrice = 0;
  let includedSms = 100;

  if (calcUsers > 5000) {
    suggestedPlan = 'Premium';
    basePrice = 3999;
    includedSms = 10000;
  } else if (calcUsers > 1000) {
    suggestedPlan = 'Pro';
    basePrice = 1599;
    includedSms = 3000;
  } else if (calcUsers > 50) {
    suggestedPlan = 'Standard';
    basePrice = 799;
    includedSms = 1000;
  } else {
    suggestedPlan = 'Free';
    basePrice = 0;
    includedSms = 100;
  }

  const extraSms = Math.max(0, calcSms - includedSms);
  const smsCost = extraSms * 0.99;
  const waCost = calcWa * 0.99;
  const estimatedTotal = Math.round(basePrice + smsCost + waCost);

  return (
    <section className="section-dark" id="pricing">
      <div className="container">
        
        {/* Section Header */}
        <div className="section-header" style={{ textAlign: 'center', maxWidth: 840, margin: '0 auto 48px' }}>
          <div className="eyebrow" style={{ justifyContent: 'center', display: 'flex', gap: '8px' }}>
            Transparent pricing in INR (₹)
          </div>
          <h2 className="display-large" style={{ marginTop: 12 }}>
            Honest plans for growing software. <br />
            <span style={{ color: '#6D7CFF' }}>
              Zero dark patterns.
            </span>
          </h2>
          <p className="body-lg" style={{ marginTop: 14 }}>
            Start completely free with strictly up to 50 active users and 100 free SMS included every month. 
            Scale seamlessly with transparent INR pricing. No surprise tier jumps, no captive password hashes.
          </p>
        </div>

        {/* 5 Pricing Tier Cards */}
        <div className="pricing-tiers-5-grid">
          
          {/* Tier 1: Free */}
          <div className="card-pricing">
            <div>
              <div className="pricing-tier-tag">
                No credit card required
              </div>
              <h3 className="heading-md" style={{ marginTop: '10px' }}>Free</h3>
              <div className="pricing-price">
                ₹0
                <span style={{ fontSize: '0.85rem', color: '#9CA3AF' }}> / month</span>
              </div>
              <div style={{ color: '#10B981', fontSize: '0.85rem', fontWeight: 500, marginBottom: '14px' }}>
                Strictly 50 active users limit
              </div>
              <p className="caption" style={{ marginBottom: '20px', minHeight: '38px' }}>
                Ideal for hobby projects, MVPs, and early prototyping.
              </p>

              <ul className="pricing-features-list" style={{ marginBottom: '24px' }}>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span><strong style={{ color: '#FFFFFF' }}>50 active users</strong> limit</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span><strong style={{ color: '#FFFFFF' }}>100 free SMS / mo</strong> included</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>₹0.99 / SMS after 100 free</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>Unlimited Email Auth &amp; OTP</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>FIDO2 Passkeys &amp; Biometrics</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>1-Click Raw Hash Export</span>
                </li>
              </ul>
            </div>

            <Link to="/waitlist?plan=free" className="button-secondary" style={{ width: '100%', textAlign: 'center', justifyContent: 'center' }}>
              Start Free
            </Link>
          </div>

          {/* Tier 2: Standard */}
          <div className="card-pricing">
            <div>
              <div className="pricing-tier-tag">
                Freelancers &amp; Apps
              </div>
              <h3 className="heading-md" style={{ marginTop: '10px' }}>Standard</h3>
              <div className="pricing-price">
                ₹799
                <span style={{ fontSize: '0.85rem', color: '#9CA3AF' }}> / month</span>
              </div>
              <div style={{ color: '#8C98FF', fontSize: '0.85rem', fontWeight: 500, marginBottom: '14px' }}>
                Up to 1,000 active users
              </div>
              <p className="caption" style={{ marginBottom: '20px', minHeight: '38px' }}>
                Full production identity with custom domain branding.
              </p>

              <ul className="pricing-features-list" style={{ marginBottom: '24px' }}>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span><strong style={{ color: '#FFFFFF' }}>1,000 active users</strong> included</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span><strong style={{ color: '#FFFFFF' }}>1,000 free SMS / mo</strong> included</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>₹0.99 / SMS after allowance</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>Custom domain (<code>auth.yourdomain.in</code>)</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>Custom SMTP / SES Email</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>15m Lockout defense</span>
                </li>
              </ul>
            </div>

            <Link to="/waitlist?plan=standard" className="button-secondary" style={{ width: '100%', textAlign: 'center', justifyContent: 'center' }}>
              Select Standard
            </Link>
          </div>

          {/* Tier 3: Pro (Featured) */}
          <div className="card-pricing-featured">
            <div>
              <div className="pricing-tier-tag featured-tier-tag">
                Most popular for B2B
              </div>
              <h3 className="heading-md" style={{ marginTop: '10px' }}>Pro</h3>
              <div className="pricing-price">
                ₹1,599
                <span style={{ fontSize: '0.85rem', color: '#9CA3AF' }}> / month</span>
              </div>
              <div style={{ color: '#6D7CFF', fontSize: '0.85rem', fontWeight: 500, marginBottom: '14px' }}>
                Up to 5,000 active users
              </div>
              <p className="caption" style={{ marginBottom: '20px', minHeight: '38px' }}>
                Enterprise SAML SSO and automated telephony fraud defense.
              </p>

              <ul className="pricing-features-list" style={{ marginBottom: '24px' }}>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span><strong style={{ color: '#FFFFFF' }}>5,000 active users</strong> included</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span><strong style={{ color: '#FFFFFF' }}>3,000 free SMS / mo</strong> included</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>₹0.99 / SMS after allowance</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span><strong style={{ color: '#FFFFFF' }}>Enterprise SAML SSO</strong> (1 conn incl.)</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>SMS Toll-Fraud Circuit Breaker</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>Multi-Tenant Tenant-Routing</span>
                </li>
              </ul>
            </div>

            <Link to="/waitlist?plan=pro" className="button-primary" style={{ width: '100%', textAlign: 'center', justifyContent: 'center' }}>
              Upgrade to Pro
            </Link>
          </div>

          {/* Tier 4: Premium */}
          <div className="card-pricing">
            <div>
              <div className="pricing-tier-tag">
                Scale &amp; SLA
              </div>
              <h3 className="heading-md" style={{ marginTop: '10px' }}>Premium</h3>
              <div className="pricing-price">
                ₹3,999
                <span style={{ fontSize: '0.85rem', color: '#9CA3AF' }}> / month</span>
              </div>
              <div style={{ color: '#8C98FF', fontSize: '0.85rem', fontWeight: 500, marginBottom: '14px' }}>
                Up to 15,000 active users
              </div>
              <p className="caption" style={{ marginBottom: '20px', minHeight: '38px' }}>
                High-throughput auth cluster with guaranteed 99.99% edge SLA.
              </p>

              <ul className="pricing-features-list" style={{ marginBottom: '24px' }}>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span><strong style={{ color: '#FFFFFF' }}>15,000 active users</strong> included</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span><strong style={{ color: '#FFFFFF' }}>10,000 free SMS / mo</strong> included</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>₹0.99 / SMS after allowance</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span><strong style={{ color: '#FFFFFF' }}>5 SAML SSO Connections</strong></span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>99.99% Edge JWKS SLA</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>Private Slack / Discord channel</span>
                </li>
              </ul>
            </div>

            <Link to="/waitlist?plan=premium" className="button-secondary" style={{ width: '100%', textAlign: 'center', justifyContent: 'center' }}>
              Select Premium
            </Link>
          </div>

          {/* Tier 5: Enterprise (Contact for Quote) */}
          <div className="card-pricing">
            <div>
              <div className="pricing-tier-tag">
                Dedicated / On-Prem
              </div>
              <h3 className="heading-md" style={{ marginTop: '10px' }}>Enterprise</h3>
              <div className="pricing-price">
                Custom
                <span style={{ fontSize: '0.85rem', color: '#9CA3AF' }}> Quote</span>
              </div>
              <div style={{ color: '#D7D7D7', fontSize: '0.85rem', fontWeight: 500, marginBottom: '14px' }}>
                Unlimited volume &amp; compliance
              </div>
              <p className="caption" style={{ marginBottom: '20px', minHeight: '38px' }}>
                Dedicated VPC clusters, on-prem Kubernetes, or custom terms.
              </p>

              <ul className="pricing-features-list" style={{ marginBottom: '24px' }}>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>Unlimited active users</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>Dedicated Cloud VPC or K8s</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>BYO Twilio &amp; Meta credentials</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>99.999% HA Cluster Uptime SLA</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>Dedicated Account Manager &amp; 24/7 Phone</span>
                </li>
                <li className="pricing-feature-item">
                  <Check size={16} strokeWidth={2.5} className="check-icon" />
                  <span>Custom BAA, DPA &amp; Security Audits</span>
                </li>
              </ul>
            </div>

            <Link to="/waitlist?plan=enterprise" className="button-secondary" style={{ width: '100%', textAlign: 'center', justifyContent: 'center' }}>
              Contact for Quote
            </Link>
          </div>

        </div>

        {/* Channel Add-ons Breakdown Strip */}
        <div className="pricing-addons-container">
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <span className="eyebrow">Wholesale channels</span>
            <h3 style={{ fontSize: '1.4rem', color: '#FFFFFF', fontWeight: 400, marginTop: '6px' }}>
              Communication add-ons &amp; wholesale rates
            </h3>
            <p style={{ color: '#D7D7D7', fontSize: '0.92rem', marginTop: '4px' }}>
              Pay strictly for the messages your end-users receive with zero artificial markup.
            </p>
          </div>

          <div className="pricing-addons-grid">
            
            {/* WhatsApp Card */}
            <div style={{ background: '#121212', border: '1px solid #374151', borderRadius: '8px', padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ background: 'rgba(16, 185, 129, 0.12)', color: '#10B981', padding: '8px', borderRadius: '8px', display: 'flex' }}>
                  <MessageSquare size={18} />
                </div>
                <div>
                  <h4 style={{ margin: 0, color: '#FFFFFF', fontSize: '1rem', fontWeight: 500 }}>WhatsApp Business OTP</h4>
                  <span style={{ fontSize: '0.78rem', color: '#10B981', fontWeight: 500 }}>Available on all plans</span>
                </div>
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 400, color: '#FFFFFF', margin: '12px 0 8px' }}>
                ₹0.99 <span style={{ fontSize: '0.85rem', color: '#9CA3AF' }}>/ delivered msg (99 paise)</span>
              </div>
              <p style={{ color: '#D7D7D7', fontSize: '0.88rem', lineHeight: 1.6 }}>
                Direct Meta Cloud API integration. 98% delivery rate, immune to carrier telephony pumping and international toll-fraud.
              </p>
            </div>

            {/* Carrier SMS Card */}
            <div style={{ background: '#121212', border: '1px solid #374151', borderRadius: '8px', padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ background: 'rgba(109, 124, 255, 0.12)', color: '#6D7CFF', padding: '8px', borderRadius: '8px', display: 'flex' }}>
                  <Phone size={18} />
                </div>
                <div>
                  <h4 style={{ margin: 0, color: '#FFFFFF', fontSize: '1rem', fontWeight: 500 }}>Carrier SMS OTP</h4>
                  <span style={{ fontSize: '0.78rem', color: '#6D7CFF', fontWeight: 500 }}>100 free / mo on free plan</span>
                </div>
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 400, color: '#FFFFFF', margin: '12px 0 8px' }}>
                ₹0.99 <span style={{ fontSize: '0.85rem', color: '#9CA3AF' }}>/ SMS after allowance</span>
              </div>
              <p style={{ color: '#D7D7D7', fontSize: '0.88rem', lineHeight: 1.6 }}>
                Includes 100 free SMS/mo on Free, 1,000 on Standard, 3,000 on Pro, and 10,000 on Premium. Protected by automated SMS circuit breaker.
              </p>
            </div>

            {/* Email & Passkeys Card */}
            <div style={{ background: '#121212', border: '1px solid #374151', borderRadius: '8px', padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ background: 'rgba(255, 255, 255, 0.08)', color: '#FFFFFF', padding: '8px', borderRadius: '8px', display: 'flex' }}>
                  <Sparkles size={18} />
                </div>
                <div>
                  <h4 style={{ margin: 0, color: '#FFFFFF', fontSize: '1rem', fontWeight: 500 }}>Email &amp; Biometric Passkeys</h4>
                  <span style={{ fontSize: '0.78rem', color: '#9CA3AF', fontWeight: 500 }}>Always free on all tiers</span>
                </div>
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 400, color: '#FFFFFF', margin: '12px 0 8px' }}>
                ₹0.00 <span style={{ fontSize: '0.85rem', color: '#9CA3AF' }}>/ unlimited</span>
              </div>
              <p style={{ color: '#D7D7D7', fontSize: '0.88rem', lineHeight: 1.6 }}>
                Email verification codes, magic links, and FIDO2 FaceID / TouchID WebAuthn passkeys are 100% free with zero metered charges.
              </p>
            </div>

          </div>
        </div>

        {/* Quick Estimator Tool */}
        <div className="pricing-estimator-container">
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <span className="eyebrow">Cost calculator</span>
            <h3 style={{ fontSize: '1.35rem', color: '#FFFFFF', fontWeight: 400, marginTop: '6px' }}>
              Interactive monthly cost estimator
            </h3>
            <p style={{ color: '#D7D7D7', fontSize: '0.9rem' }}>
              Drag the sliders below to see your recommended plan and estimated monthly invoice in INR.
            </p>
          </div>

          <div className="pricing-estimator-grid">
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <label style={{ fontSize: '0.88rem', color: '#D7D7D7' }}>Active Monthly Users</label>
                <span style={{ fontWeight: 500, color: '#FFFFFF' }}>{calcUsers.toLocaleString()} users</span>
              </div>
              <input 
                type="range" 
                min="10" 
                max="10000" 
                step="50"
                value={calcUsers} 
                onChange={(e) => setCalcUsers(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#6D7CFF' }}
              />

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', marginBottom: '8px' }}>
                <label style={{ fontSize: '0.88rem', color: '#D7D7D7' }}>Monthly SMS Sent</label>
                <span style={{ fontWeight: 500, color: '#FFFFFF' }}>{calcSms} SMS</span>
              </div>
              <input 
                type="range" 
                min="0" 
                max="2000" 
                step="25"
                value={calcSms} 
                onChange={(e) => setCalcSms(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#6D7CFF' }}
              />

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', marginBottom: '8px' }}>
                <label style={{ fontSize: '0.88rem', color: '#D7D7D7' }}>Monthly WhatsApp OTPs</label>
                <span style={{ fontWeight: 500, color: '#FFFFFF' }}>{calcWa} msgs</span>
              </div>
              <input 
                type="range" 
                min="0" 
                max="2000" 
                step="25"
                value={calcWa} 
                onChange={(e) => setCalcWa(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#6D7CFF' }}
              />
            </div>

            <div style={{ background: '#121212', border: '1px solid #374151', borderRadius: '8px', padding: '28px', color: '#FFFFFF', textAlign: 'center' }}>
              <span className="eyebrow" style={{ color: '#6D7CFF' }}>Recommended plan</span>
              <div style={{ fontSize: '1.5rem', fontWeight: 400, color: '#FFFFFF', marginTop: '4px' }}>
                Draway {suggestedPlan}
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: 400, color: '#FFFFFF', margin: '12px 0 6px' }}>
                ₹{estimatedTotal.toLocaleString()}
                <span style={{ fontSize: '0.88rem', color: '#9CA3AF' }}> / month</span>
              </div>
              <p style={{ color: '#D7D7D7', fontSize: '0.82rem', lineHeight: 1.5, margin: '0 auto 20px', maxWidth: '300px' }}>
                Base: ₹{basePrice} | Included SMS: {includedSms} | Extra SMS: ₹{smsCost.toFixed(2)} | WhatsApp: ₹{waCost.toFixed(2)}
              </p>
              <Link to={`/waitlist?plan=${suggestedPlan.toLowerCase()}`} className="button-primary" style={{ display: 'inline-flex', width: '100%' }}>
                Choose {suggestedPlan}
              </Link>
            </div>
          </div>
        </div>

        {/* 5 Invariant Anti-Dark-Pattern Commitments */}
        <div className="pricing-guarantees-container">
          <div style={{ textAlign: 'center', marginBottom: '28px' }}>
            <span className="eyebrow">Developer integrity</span>
            <h3 style={{ fontSize: '1.4rem', color: '#FFFFFF', fontWeight: 400, marginTop: '6px' }}>
              Our 5 anti-dark-pattern guarantees
            </h3>
            <p style={{ color: '#D7D7D7', fontSize: '0.92rem', maxWidth: '640px', margin: '4px auto 0' }}>
              Identity infrastructure is the heart of your SaaS. We treat pricing transparency with cryptographic seriousness.
            </p>
          </div>

          <div className="pricing-guarantees-grid">
            
            <div style={{ padding: '18px', background: '#121212', border: '1px solid #374151', borderRadius: '8px' }}>
              <div style={{ color: '#6D7CFF', marginBottom: '10px' }}><ShieldCheck size={20} /></div>
              <h4 style={{ margin: '0 0 6px', color: '#FFFFFF', fontSize: '0.92rem', fontWeight: 500 }}>1. No Silent Upgrades</h4>
              <p style={{ color: '#D7D7D7', fontSize: '0.84rem', lineHeight: 1.5, margin: 0 }}>
                If you approach your active user limit, we notify you in the console. We never quietly charge overages or force automatic tier bumps.
              </p>
            </div>

            <div style={{ padding: '18px', background: '#121212', border: '1px solid #374151', borderRadius: '8px' }}>
              <div style={{ color: '#6D7CFF', marginBottom: '10px' }}><Zap size={20} /></div>
              <h4 style={{ margin: '0 0 6px', color: '#FFFFFF', fontSize: '0.92rem', fontWeight: 500 }}>2. No Card for Free</h4>
              <p style={{ color: '#D7D7D7', fontSize: '0.84rem', lineHeight: 1.5, margin: 0 }}>
                Get started with strictly 50 users and 100 free SMS indefinitely without ever typing a credit card number.
              </p>
            </div>

            <div style={{ padding: '18px', background: '#121212', border: '1px solid #374151', borderRadius: '8px' }}>
              <div style={{ color: '#6D7CFF', marginBottom: '10px' }}><Lock size={20} /></div>
              <h4 style={{ margin: '0 0 6px', color: '#FFFFFF', fontSize: '0.92rem', fontWeight: 500 }}>3. Zero Hostage Hashes</h4>
              <p style={{ color: '#D7D7D7', fontSize: '0.84rem', lineHeight: 1.5, margin: 0 }}>
                1-Click self-serve export of your raw Argon2id password hashes and salts is free and guaranteed on all tiers, including Free.
              </p>
            </div>

            <div style={{ padding: '18px', background: '#121212', border: '1px solid #374151', borderRadius: '8px' }}>
              <div style={{ color: '#6D7CFF', marginBottom: '10px' }}><Check size={20} /></div>
              <h4 style={{ margin: '0 0 6px', color: '#FFFFFF', fontSize: '0.92rem', fontWeight: 500 }}>4. 1-Click Cancel</h4>
              <p style={{ color: '#D7D7D7', fontSize: '0.84rem', lineHeight: 1.5, margin: 0 }}>
                Cancel or downgrade your plan directly from the web console. No awkward sales retention calls or cancellation hoops.
              </p>
            </div>

            <div style={{ padding: '18px', background: '#121212', border: '1px solid #374151', borderRadius: '8px' }}>
              <div style={{ color: '#6D7CFF', marginBottom: '10px' }}><Building2 size={20} /></div>
              <h4 style={{ margin: '0 0 6px', color: '#FFFFFF', fontSize: '0.92rem', fontWeight: 500 }}>5. Clear INR Billing</h4>
              <p style={{ color: '#D7D7D7', fontSize: '0.84rem', lineHeight: 1.5, margin: 0 }}>
                Billed cleanly in Indian Rupees (₹) with simple GST-compliant invoices and zero hidden foreign conversion fees.
              </p>
            </div>

          </div>
        </div>

        {/* Action Button */}
        <div style={{ textAlign: 'center' }}>
          <Link to="/waitlist" className="button-primary" style={{ padding: '14px 28px', fontSize: '15px' }}>
            Start free with 50 users <ArrowRight size={16} />
          </Link>
        </div>

      </div>
    </section>
  );
};

export default Pricing;
